import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { forgotPassword, apiError } from "../../lib/api";

const FIELD = "w-full border border-border bg-secondary px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-brand";

export default function ForgotPasswordDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email.");
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const close = (o) => {
    onOpenChange(o);
    if (!o) { setSent(false); setEmail(""); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="border-border bg-card sm:max-w-md" data-testid="forgot-password-dialog">
        {sent ? (
          <div className="py-4 text-center" data-testid="forgot-password-sent">
            <CheckCircle2 size={38} className="mx-auto text-brand" />
            <h3 className="mt-5 font-display text-2xl tracking-tight text-navy">Check your email.</h3>
            <p className="mt-3 text-sm text-muted-foreground">If an account exists for <strong>{email}</strong>, we've sent a secure link to reset your password. It expires in 30 minutes.</p>
            <button onClick={() => close(false)} data-testid="forgot-password-close" className="mt-6 inline-flex items-center justify-center border border-border px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors hover:border-navy">Done</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 text-brand"><Mail size={16} /><span className="font-mono text-[11px] uppercase tracking-[0.2em]">Password reset</span></div>
              <DialogTitle className="mt-2 font-display text-2xl font-light tracking-tighter text-navy">Forgot your password?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">Enter the email on your account and we'll send you a secure link to set a new password.</p>
            <form onSubmit={submit} className="mt-2 space-y-4">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your account email" className={FIELD} data-testid="forgot-password-email" />
              <button type="submit" disabled={loading} data-testid="forgot-password-submit" className="inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Send reset link
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
